import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { makeUseAxios, UseAxios } from 'axios-hooks'
import { ApiProps, DOMAIN } from 'constants/api'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import { JwtContext } from 'contexts/JwtContext'
import { useContext } from 'react'
import { sendLog } from 'utils/log'
import { fail, StatusProps, success } from 'utils/status'

/**
 * エラー時のログ送信機能をオフにしたuseAxiosを生成する。
 * @returns カスタマイズされたuseAxios
 */
export const useAxios = (): UseAxios =>
  makeUseAxios({
    // hooksの生成
    axios: axios.create({
      baseURL: DOMAIN,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      responseType: 'json',
    }),
  })

/**
 * メソッドに応じてpayloadの送信方法を変更する。
 * @param api API定義
 * @param payload 送信ペイロード
 * @returns ペイロード格納オブジェクト
 */
const getOptionsWithPayload = (
  api: ApiProps,
  payload: any
): AxiosRequestConfig | undefined => {
  switch (api.config.method) {
    case 'GET':
      return {
        params: payload,
      }
    case 'POST':
      return {
        data: payload,
      }
  }
}

/**
 * API定義を利用したuseAxiosを生成する。
 * @param api API定義
 * @returns API定義を利用してカスタマイズされたuseAxios
 */
export const useSender = (
  api: ApiProps
): [
  [any, boolean, AxiosError<any> | undefined],
  (data: any) => Promise<StatusProps>
] => {
  const [jwt] = useContext(JwtContext)
  const [{ data, loading, error }, send] = useAxios()(
    {
      ...api.config,
      headers: {
        Authorization: jwt || undefined,
      },
    },
    api.options
  )
  const historyStackContext = useContext(HistoryStackContext)

  // axios-hooksの送信処理にpayloadを渡すだけのラッパーを生成
  const sender = async (payload: any): Promise<any> => {
    const options = getOptionsWithPayload(api, payload)
    try {
      // axiosの通信本体
      const result = await send(options)
      return success(result)
    } catch (error) {
      console.error(error.toString())
      if (!api.disableErrorLog) {
        // エラー時、ログ送信対象ならログスタックを送信
        sendLog(historyStackContext, error.toString(), jwt)
      }
      return fail(error, error.message)
    }
  }

  // hooksを使えるよう返却
  return [[data, loading, error], sender]
}
